import { useState } from 'react'
import { STATUS_CATEGORIES, type StatusCategory, type StatusCode, type StatusCodeValues } from '../../pages/admin/catalogStore'

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

/**
 * A status code's short label, its category, whether it counts as available,
 * and its place in the list. The coverage calculation reads the category and
 * the availability, so a change to either applies to every schedule that uses
 * the code, past and future; the form says how many entries that is. The code
 * and name are shown as they are: the scheduling functions find a status code
 * by them.
 */
export default function StatusCodeForm({
  code,
  saving,
  error,
  onSave,
  onCancel,
}: {
  code: StatusCode
  saving: boolean
  error: string | null
  onSave: (values: StatusCodeValues) => void
  onCancel: () => void
}) {
  const [shortLabel, setShortLabel] = useState(code.shortLabel)
  const [category, setCategory] = useState<StatusCategory>(code.category)
  const [countsAsAvailable, setCountsAsAvailable] = useState(code.countsAsAvailable)
  const [sortOrder, setSortOrder] = useState(String(code.sortOrder))

  const order = sortOrder === '' ? NaN : Number(sortOrder)
  const changed =
    shortLabel.trim() !== code.shortLabel ||
    category !== code.category ||
    countsAsAvailable !== code.countsAsAvailable ||
    order !== code.sortOrder
  const canSave = shortLabel.trim() !== '' && Number.isInteger(order) && order >= 0 && changed
  const changesCoverage = category !== code.category || countsAsAvailable !== code.countsAsAvailable

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label={`Edit ${code.name}`}
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave && !saving) onSave({ shortLabel: shortLabel.trim(), category, countsAsAvailable, sortOrder: order })
      }}
    >
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-small">
        <dt className="text-ink-muted">Name</dt>
        <dd className="font-bold">{code.name}</dd>
        <dt className="text-ink-muted">Code</dt>
        <dd className="font-medium">{code.code}</dd>
      </dl>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor="code-short-label" className={LABEL}>
            Short label
          </label>
          <input id="code-short-label" type="text" maxLength={20} value={shortLabel} onChange={(event) => setShortLabel(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="code-category" className={LABEL}>
            Category
          </label>
          <select id="code-category" value={category} onChange={(event) => setCategory(event.target.value as StatusCategory)} className={FIELD}>
            {STATUS_CATEGORIES.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="code-order" className={LABEL}>
            Order in the list
          </label>
          <input id="code-order" type="number" min={0} step={1} value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} className={FIELD} />
        </div>
      </div>

      <label className="flex min-h-11 items-center gap-2 text-small">
        <input type="checkbox" checked={countsAsAvailable} onChange={(event) => setCountsAsAvailable(event.target.checked)} className="size-5" />
        Counts as available in coverage
      </label>

      {changesCoverage && (
        <p role="status" className="text-small text-warning">
          {code.entries === 0
            ? 'No schedule entries use this code yet. Coverage reads the new setting from now on.'
            : `${code.entries} schedule ${code.entries === 1 ? 'entry uses' : 'entries use'} this code. Coverage reads the new setting for every one of them, past and future.`}
        </p>
      )}

      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
